import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogOut, ShieldX, ArrowLeft } from 'lucide-react';

const AccessDenied = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shadow-sm">
        <div className="flex items-center space-x-3">
          <span className="text-lg font-semibold text-gray-800">GroundAale</span>
        </div>
        <div className="flex items-center space-x-3">
          {user && (
            <span className="text-sm text-gray-600">
              {user.name || user.username} ({user.role_name || 'No role'})
            </span>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center space-x-1.5 px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium"
          >
            <LogOut className="h-4 w-4" />
            <span>Logout</span>
          </button>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <ShieldX className="h-8 w-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">
            You don't have permission to access this page. Please contact your administrator
            to get the right permissions or facility assignment.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleGoBack}
              className="flex items-center justify-center space-x-1.5 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Go Back</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center justify-center space-x-1.5 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium"
            >
              <LogOut className="h-4 w-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const NoRoleAssigned = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shadow-sm">
        <span className="text-lg font-semibold text-gray-800">GroundAale</span>
        <div className="flex items-center space-x-3">
          {user && (
            <span className="text-sm text-gray-600">
              {user.name || user.username}
            </span>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center space-x-1.5 px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium"
          >
            <LogOut className="h-4 w-4" />
            <span>Logout</span>
          </button>
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
            <ShieldX className="h-8 w-8 text-amber-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No Role Assigned</h2>
          <p className="text-gray-600 mb-6">
            Your account doesn't have a role or permissions configured yet.
            Please contact your administrator to assign a role and facility access.
          </p>
          <button
            onClick={handleLogout}
            className="flex items-center justify-center space-x-1.5 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium mx-auto"
          >
            <LogOut className="h-4 w-4" />
            <span>Sign Out</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// `anyOf` — list of { module, accessType } the caller may satisfy *any* of.
// Used where one screen is reachable through more than one permission, e.g.
// booking a secondary service needs `individual_booking:write` OR `bookings:write`
// (issue #187). When `anyOf` is set, `module`/`accessType` are ignored.
const ProtectedRoute = ({ children, module, accessType = 'read', anyOf }) => {
  const { user, isAuthenticated, loading, isSuperAdmin, hasPermission } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (isSuperAdmin) {
    return <Navigate to="/superadmin/facilities" replace />;
  }

  if (Array.isArray(anyOf) && anyOf.length > 0) {
    if (!anyOf.some((opt) => hasPermission(opt.module, opt.accessType || 'read'))) {
      return <AccessDenied />;
    }
  } else if (module && !hasPermission(module, accessType)) {
    return <AccessDenied />;
  }

  if (!user?.role_name || !user?.permissions || Object.keys(user.permissions).length === 0) {
    return <NoRoleAssigned />;
  }

  return children;
};

export default ProtectedRoute;
